/** Clickable ticker links inside daily briefing copy. */

import React from "react";
import { formatPrimeEntryLevel } from "./primeSetupFlag.js";

const TICKER_RE = /\b([A-Z]{1,5})\b/g;

export function TodaysPrimeSetupsBlock({ watchlist = [], onTickerClick, T, companyNames = {} }) {
  const primes = (watchlist || [])
    .filter((i) => i.result?.isPrime === true)
    .sort((a, b) => (b.result?.buyScore ?? -99) - (a.result?.buyScore ?? -99));

  return (
    <div
      style={{
        marginBottom: 12,
        padding: "10px 12px",
        borderRadius: 8,
        background: "linear-gradient(180deg, #fef9c322 0%, #fef3c710 100%)",
        border: "1px solid #f59e0b55",
      }}
    >
      <div style={{ fontSize: 10, fontWeight: 800, color: "#b45309", letterSpacing: "0.08em", marginBottom: 8 }}>
        ⭐ TODAY&apos;S PRIME SETUPS
      </div>
      {primes.length === 0 ? (
        <div style={{ fontSize: 12, color: T.textSub, lineHeight: 1.65 }}>
          No prime setups today — market has no clean entries. Consider waiting.
        </div>
      ) : (
        <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 10 }}>
          {primes.map(({ symbol, result }) => {
            const reasons = (result.primeReasons || []).filter(Boolean);
            const entry = formatPrimeEntryLevel(result);
            const t1 = result.t1;
            const name = companyNames[symbol] || symbol;
            return (
              <li key={symbol} style={{ fontSize: 12, color: T.text, lineHeight: 1.55 }}>
                <button
                  type="button"
                  onClick={() => onTickerClick?.(symbol)}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    margin: 0,
                    font: "inherit",
                    fontWeight: 800,
                    color: "#b45309",
                    cursor: "pointer",
                    textDecoration: "underline",
                    textUnderlineOffset: 2,
                  }}
                >
                  {symbol}
                </button>
                {name !== symbol && (
                  <span style={{ color: T.textDim, fontWeight: 500 }}> · {name}</span>
                )}
                <div style={{ marginTop: 4, fontSize: 11, color: T.textSub }}>
                  <strong style={{ color: T.text }}>Why:</strong>{" "}
                  {reasons.length ? reasons.join(" · ") : "Prime criteria met"}
                </div>
                <div style={{ fontSize: 11, color: T.textSub }}>
                  <strong style={{ color: T.text }}>Entry:</strong> {entry}
                  {" · "}
                  <strong style={{ color: T.text }}>T1:</strong>{" "}
                  {t1 != null ? `$${t1.toFixed(2)}` : "—"}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function BriefingParagraph({ text, watchlistSymbols, onTickerClick, T }) {
  if (!text) return null;
  const allowed = new Set((watchlistSymbols || []).map((s) => String(s).toUpperCase()));
  const parts = [];
  let last = 0;
  let m;
  const re = new RegExp(TICKER_RE.source, "g");
  while ((m = re.exec(text)) !== null) {
    const sym = m[1];
    const start = m.index;
    if (start > last) parts.push({ type: "text", value: text.slice(last, start) });
    if (allowed.has(sym)) {
      parts.push({ type: "ticker", value: sym });
    } else {
      parts.push({ type: "text", value: sym });
    }
    last = start + sym.length;
  }
  if (last < text.length) parts.push({ type: "text", value: text.slice(last) });

  return (
    <p style={{ fontSize: 12, color: T.text, lineHeight: 1.8, margin: 0 }}>
      {parts.map((p, i) =>
        p.type === "ticker" ? (
          <button
            key={`${p.value}-${i}`}
            type="button"
            onClick={() => onTickerClick?.(p.value)}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              margin: 0,
              font: "inherit",
              fontWeight: 800,
              color: T.cyan,
              cursor: "pointer",
              textDecoration: "underline",
              textUnderlineOffset: 2,
            }}
          >
            {p.value}
          </button>
        ) : (
          <span key={`t-${i}`}>{p.value}</span>
        )
      )}
    </p>
  );
}
